/* Blog listing (sections/ex-blog-listing.liquid): filters, search and sort over the markup already on the page. State is kept in the address. */
if (!customElements.get('ex-blog-listing')) {
  customElements.define(
    'ex-blog-listing',
    class ExBlogListing extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.ex-blog-listing__item'));
        this.checkboxes = Array.from(this.querySelectorAll('.ex-blog-filters__checkbox'));
        this.searchInput = this.querySelector('[data-ex-blog-search]');
        this.sortSelect = this.querySelector('[data-ex-blog-sort]');
        this.countEl = this.querySelector('[data-ex-blog-count]');
        this.emptyEl = this.querySelector('[data-ex-blog-empty]');
        this.grid = this.querySelector('.ex-blog-listing__grid');
        this.resetButton = this.querySelector('[data-ex-blog-reset]');
        this.paginationEl = this.querySelector('[data-ex-blog-pagination]');
        this.perPage = this.paginationEl ? parseInt(this.paginationEl.dataset.perPage, 10) || 0 : 0;
        this.page = 1;

        // "[count] Blogs" — kept as a template so the wording stays a theme setting
        this.countTemplate = this.countEl ? this.countEl.textContent.trim().replace(/^\d+/, '[count]') : '';
        this.order = new Map(this.items.map((item, index) => [item, index]));

        this.setUpPanel();
        this.setUpGroups();
        this.readAddress();
        this.bind();
        this.apply({ push: false });
      }

      /* ---------- The filter panel ---------- */

      /* Below 990px the panel is a drawer over the page; the desktop media query owns that width. */
      setUpPanel() {
        const side = this.querySelector('[data-ex-blog-side]');
        const button = this.querySelector('.ex-blog-filters__open');
        if (!side || !button) return;

        this.side = side;
        this.openButton = button;
        this.overlay = this.querySelector('[data-ex-blog-overlay]');
        this.closeButton = this.querySelector('[data-ex-blog-close]');
        this.desktop = window.matchMedia('(min-width: 990px)');

        side.setAttribute('data-filters-open', 'false');

        button.addEventListener('click', () => {
          if (button.getAttribute('aria-expanded') === 'true') this.closePanel();
          else this.openPanel();
        });

        if (this.closeButton) this.closeButton.addEventListener('click', () => this.closePanel());
        if (this.overlay) this.overlay.addEventListener('click', () => this.closePanel());

        this.onKeydown = (event) => {
          if (event.key === 'Escape') this.closePanel();
        };

        // Widening the window past the breakpoint leaves the drawer behind, so let the page go
        const onDesktop = () => {
          if (this.desktop.matches) this.closePanel();
        };
        if (this.desktop.addEventListener) this.desktop.addEventListener('change', onDesktop);
        else this.desktop.addListener(onDesktop);
      }

      openPanel() {
        if (!this.side || this.desktop.matches) return;
        this.openButton.setAttribute('aria-expanded', 'true');
        this.side.setAttribute('data-filters-open', 'true');

        // The page behind must not scroll under the drawer
        this.bodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        document.addEventListener('keydown', this.onKeydown);

        /* Flush the new styles, so nothing inside the panel is asked to take focus while it is hidden. */
        if (this.closeButton) {
          const panel = this.querySelector('.ex-blog-filters');
          if (panel) void panel.offsetWidth;
          this.closeButton.focus();
        }
      }

      closePanel() {
        if (!this.side) return;
        const wasOpen = this.openButton.getAttribute('aria-expanded') === 'true';
        this.openButton.setAttribute('aria-expanded', 'false');
        this.side.setAttribute('data-filters-open', 'false');
        document.body.style.overflow = this.bodyOverflow || '';
        document.removeEventListener('keydown', this.onKeydown);

        // Only pull focus back if the drawer actually had it
        if (wasOpen && !this.desktop.matches && this.contains(document.activeElement)) {
          this.openButton.focus();
        }
      }

      /* The button is the only sign of an active filter once the panel is a drawer */
      updateFilterCount() {
        if (!this.openButton) return;
        const label = this.openButton.querySelector('span:first-child');
        if (!label) return;

        let count = this.openButton.querySelector('.ex-blog-filters__count');
        const on = this.checkboxes.filter((box) => box.checked).length;

        if (!on) {
          if (count) count.remove();
          return;
        }
        if (!count) {
          count = document.createElement('span');
          count.className = 'ex-blog-filters__count';
          label.appendChild(count);
        }
        count.textContent = on;
      }

      setUpGroups() {
        this.querySelectorAll('.ex-blog-filters__toggle').forEach((toggle) => {
          toggle.addEventListener('click', () => {
            toggle.setAttribute('aria-expanded', toggle.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
          });
        });
      }

      /* ---------- Reading and writing the address ---------- */

      readAddress() {
        const params = new URLSearchParams(window.location.search);

        this.checkboxes.forEach((box) => {
          const chosen = (params.get(box.name) || '').split(',').filter(Boolean);
          box.checked = chosen.includes(box.value);
        });

        if (this.searchInput && params.get('q')) this.searchInput.value = params.get('q');

        if (this.sortSelect && params.get('tri')) {
          const wanted = params.get('tri');
          if (Array.from(this.sortSelect.options).some((option) => option.value === wanted)) {
            this.sortSelect.value = wanted;
          }
        }

        const page = parseInt(params.get('page'), 10);
        if (page > 0) this.page = page;

        /* The panel no longer springs open on arrival; the count on the button carries the message. */
        this.updateFilterCount();
      }

      writeAddress() {
        const params = new URLSearchParams(window.location.search);
        const groups = this.chosenTags();

        // Only our own keys are touched, so anything else in the address survives
        this.filterKeys().forEach((key) => {
          const values = groups[key];
          if (values && values.length) {
            params.set(key, values.join(','));
          } else {
            params.delete(key);
          }
        });

        const term = this.searchInput ? this.searchInput.value.trim() : '';
        if (term) {
          params.set('q', term);
        } else {
          params.delete('q');
        }

        const sort = this.sortSelect ? this.sortSelect.value : '';
        if (sort && sort !== this.sortSelect.options[0].value) {
          params.set('tri', sort);
        } else {
          params.delete('tri');
        }

        if (this.page > 1) {
          params.set('page', String(this.page));
        } else {
          params.delete('page');
        }

        const query = params.toString();
        window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
      }

      /* ---------- Matching ---------- */

      filterKeys() {
        return Array.from(new Set(this.checkboxes.map((box) => box.name)));
      }

      chosenTags() {
        const groups = {};
        this.checkboxes.forEach((box) => {
          if (!groups[box.name]) groups[box.name] = [];
          if (box.checked) groups[box.name].push(box.value);
        });
        return groups;
      }

      // Lower case and without accents, so "Araignées" is found by typing "araignees"
      static plain(value) {
        return (value || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '');
      }

      matches(item, groups, words) {
        const tags = (item.dataset.tags || '').split(/\s+/).filter(Boolean);

        // Every group that has a box ticked must be satisfied by one of its tags
        const passesFilters = Object.keys(groups).every((key) => {
          const chosen = groups[key];
          return chosen.length === 0 || chosen.some((tag) => tags.includes(tag));
        });
        if (!passesFilters) return false;

        if (words.length === 0) return true;
        const haystack = ExBlogListing.plain(item.dataset.search);
        return words.every((word) => haystack.includes(word));
      }

      /* ---------- Applying ---------- */

      apply(options) {
        const groups = this.chosenTags();
        const words = this.searchInput
          ? ExBlogListing.plain(this.searchInput.value).split(/\s+/).filter(Boolean)
          : [];

        // Sort first, so a page is a slice of the order the reader asked for
        this.sortItems();

        const matched = this.items.filter((item) => this.matches(item, groups, words));
        const pages = this.perPage > 0 ? Math.max(1, Math.ceil(matched.length / this.perPage)) : 1;
        if (this.page > pages) this.page = pages;
        if (this.page < 1) this.page = 1;

        const start = this.perPage > 0 ? (this.page - 1) * this.perPage : 0;
        const onPage = this.perPage > 0 ? matched.slice(start, start + this.perPage) : matched;
        const visible = new Set(onPage);

        // Cards off the page are hidden, so the browser never fetches their lazy images
        this.items.forEach((item) => {
          item.hidden = !visible.has(item);
        });

        if (this.countEl && this.countTemplate) {
          this.countEl.textContent = this.countTemplate.replace('[count]', matched.length);
        }
        if (this.emptyEl) this.emptyEl.style.display = matched.length === 0 ? 'block' : 'none';
        if (this.grid) this.grid.hidden = matched.length === 0;

        this.renderPagination(pages);

        if (!options || options.push !== false) this.writeAddress();
      }

      /* ---------- Pagination ---------- */

      // The numbers the Figma draws: the first three, a gap, then the last
      static window(current, last) {
        const shown = new Set([1, last]);
        for (let page = current - 1; page <= current + 1; page += 1) {
          if (page >= 1 && page <= last) shown.add(page);
        }
        if (current <= 3) {
          [2, 3].forEach((page) => page <= last && shown.add(page));
        }
        if (current >= last - 2) {
          [last - 1, last - 2].forEach((page) => page >= 1 && shown.add(page));
        }
        return Array.from(shown).sort((a, b) => a - b);
      }

      renderPagination(pages) {
        if (!this.paginationEl) return;
        this.paginationEl.textContent = '';
        if (pages < 2) return;

        const data = this.paginationEl.dataset;

        const arrow = (glyph, label, target) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ex-blog-pagination__arrow';
          button.textContent = glyph;
          button.setAttribute('aria-label', label);
          button.addEventListener('click', () => this.goTo(target));
          return button;
        };

        if (this.page > 1) {
          this.paginationEl.appendChild(arrow('←', data.prevLabel || 'Previous', this.page - 1));
        }

        let previous = 0;
        ExBlogListing.window(this.page, pages).forEach((page) => {
          if (previous && page - previous > 1) {
            const gap = document.createElement('span');
            gap.className = 'ex-blog-pagination__gap';
            gap.textContent = '…';
            this.paginationEl.appendChild(gap);
          }
          previous = page;

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ex-blog-pagination__page';
          button.textContent = String(page);
          button.setAttribute('aria-label', (data.pageLabel || 'Page [page]').replace('[page]', page));
          if (page === this.page) button.setAttribute('aria-current', 'page');
          button.addEventListener('click', () => this.goTo(page));
          this.paginationEl.appendChild(button);
        });

        if (this.page < pages) {
          this.paginationEl.appendChild(arrow('→', data.nextLabel || 'Next', this.page + 1));
        }
      }

      goTo(page) {
        if (page === this.page) return;
        this.page = page;
        this.apply();

        // Back to the first card, so the reader isn't left halfway down the new page
        const top = this.querySelector('.ex-blog-listing__main');
        if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      sortItems() {
        if (!this.sortSelect || !this.grid) return;
        const mode = this.sortSelect.value;

        const sorted = this.items.slice().sort((a, b) => {
          switch (mode) {
            case 'date-desc':
              return Number(b.dataset.date) - Number(a.dataset.date);
            case 'date-asc':
              return Number(a.dataset.date) - Number(b.dataset.date);
            case 'title-asc':
              return (a.dataset.title || '').localeCompare(b.dataset.title || '', 'fr');
            case 'title-desc':
              return (b.dataset.title || '').localeCompare(a.dataset.title || '', 'fr');
            default:
              // "En vedette" is the order Liquid rendered, newest first
              return this.order.get(a) - this.order.get(b);
          }
        });

        // One write, so the browser reflows the grid once
        const fragment = document.createDocumentFragment();
        sorted.forEach((item) => fragment.appendChild(item));
        this.grid.appendChild(fragment);
      }

      /* ---------- Events ---------- */

      bind() {
        // Changing what is listed sends the reader back to the first page
        const refresh = () => {
          this.page = 1;
          this.apply();
        };

        this.checkboxes.forEach((box) =>
          box.addEventListener('change', () => {
            this.updateFilterCount();
            refresh();
          })
        );

        if (this.sortSelect) this.sortSelect.addEventListener('change', refresh);

        if (this.searchInput) {
          let timer;
          const run = () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(refresh, 200);
          };
          this.searchInput.addEventListener('input', run);
          this.searchInput.addEventListener('search', run);
          // Enter would post the page's form and lose the state we keep in the address
          this.searchInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') event.preventDefault();
          });
        }

        if (this.resetButton) {
          this.resetButton.addEventListener('click', () => {
            this.checkboxes.forEach((box) => {
              box.checked = false;
            });
            if (this.searchInput) this.searchInput.value = '';
            this.page = 1;
            this.apply();
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      }
    }
  );
}
